import { Capacitor } from '@capacitor/core'

/**
 * LA UBICACIÓN DE UN SOLO TIRO, QUE FUNCIONE TAMBIÉN DENTRO DEL APK.
 *
 * El problema que resuelve: dentro del WebView de Android, el
 * navigator.geolocation del navegador NO lo maneja el permiso del sistema, sino
 * el WebChromeClient de Capacitor. Y ese cliente, si no encuentra registrado el
 * plugin @capacitor/geolocation, RECHAZA el permiso del WebView (invoke false)
 * sin importar que la app tenga el permiso de ubicación concedido. Por eso el
 * rastreo en segundo plano funcionaba pero "marcar llegada", el sello de la
 * foto y el selector de ubicación reportaban «el técnico negó el permiso»
 * cuando en realidad estaba dado.
 *
 * La solución es doble:
 *   1. Tener instalado @capacitor/geolocation (con eso el WebChromeClient ya no
 *      rechaza el navigator.geolocation del WebView).
 *   2. Además, dentro del APK pedir la posición por el plugin nativo, que no
 *      pasa por el puente del WebView y es más fiable con el GPS frío.
 *
 * En el navegador (web) todo sigue igual: se usa navigator.geolocation directo.
 *
 * Devuelve una forma con `.coords` compatible con GeolocationPosition, para que
 * quien ya leía pos.coords.latitude/longitude/accuracy no cambie nada.
 */

export type LecturaUbicacion = {
  coords: {
    latitude: number
    longitude: number
    accuracy: number
  }
}

type Opciones = {
  /** Cuánto esperar antes de rendirse. El GPS frío tarda. */
  timeout?: number
  /** Qué tan viejo se acepta un punto guardado, en ms. Cero = solo ahora. */
  maximumAge?: number
}

/** La posición por el plugin nativo de Capacitor. Solo dentro del APK. */
async function porPluginNativo(opciones: Opciones): Promise<LecturaUbicacion | null> {
  try {
    // Import dinámico: el navegador nunca carga este módulo del plugin.
    const { Geolocation } = await import('@capacitor/geolocation')

    // Asegurar el permiso del sistema. El rastreo ya suele haberlo pedido, pero
    // si el técnico entra directo a "marcar llegada" puede no estar aún.
    try {
      const estado = await Geolocation.checkPermissions()
      if (estado.location !== 'granted' && estado.coarseLocation !== 'granted') {
        const pedido = await Geolocation.requestPermissions({ permissions: ['location'] })
        if (pedido.location !== 'granted' && pedido.coarseLocation !== 'granted') {
          return null
        }
      }
    } catch {
      // Si checkPermissions no está disponible, seguimos: getCurrentPosition
      // pedirá el permiso por su cuenta.
    }

    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: opciones.timeout ?? 20_000,
      maximumAge: opciones.maximumAge ?? 0,
    })
    return {
      coords: {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      },
    }
  } catch {
    return null
  }
}

/** La posición por el navegador. Es el camino de la web y el respaldo del APK. */
function porNavegador(opciones: Opciones): Promise<LecturaUbicacion | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null)
  }
  return new Promise((resolver) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolver({
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
        }),
      () => resolver(null),
      {
        enableHighAccuracy: true,
        timeout: opciones.timeout ?? 20_000,
        maximumAge: opciones.maximumAge ?? 0,
      },
    )
  })
}

/**
 * La posición de ahora. Dentro del APK va primero por el plugin nativo y, si
 * fallara, cae al navegador; en la web usa el navegador directo.
 */
export async function obtenerPosicionActual(opciones: Opciones = {}): Promise<LecturaUbicacion | null> {
  if (Capacitor.isNativePlatform()) {
    const nativa = await porPluginNativo(opciones)
    if (nativa) return nativa
    return porNavegador(opciones)
  }
  return porNavegador(opciones)
}

/**
 * LA MEJOR POSICIÓN EN UNOS SEGUNDOS, NO LA PRIMERA.
 *
 * El ingeniero lo vio en los sellos de las fotos: «± 100 m». La primera lectura
 * que da el teléfono suele venir de wifi y antenas mientras el GPS se
 * engancha; segundos después baja a 10 m. Medido en el rastreo: de 168
 * posiciones la mediana es 9,9 m y el 90 % está por debajo de 26 m, pero
 * quien se quedaba con la primera —sello, «usar mi ubicación»— se llevaba la
 * gruesa.
 *
 * Aquí se piden lecturas frescas hasta tener una de `objetivoM` o mejor, o
 * hasta que se acabe el tiempo, y se devuelve la mejor que hubo. Nunca peor
 * que antes: si el GPS no afina, sale lo mismo que salía.
 */
export async function mejorPosicion({
  objetivoM = 25,
  maxMs = 10_000,
}: { objetivoM?: number; maxMs?: number } = {}): Promise<LecturaUbicacion | null> {
  const hasta = Date.now() + maxMs
  let mejor: LecturaUbicacion | null = null

  while (Date.now() < hasta) {
    const quedan = Math.max(1_000, hasta - Date.now())
    const lectura = await obtenerPosicionActual({ timeout: quedan, maximumAge: 0 })
    if (lectura && (!mejor || lectura.coords.accuracy < mejor.coords.accuracy)) {
      mejor = lectura
    }
    if (mejor && mejor.coords.accuracy <= objetivoM) break
    // Sin lectura (GPS apagado, sin permiso): insistir no la va a traer.
    if (!lectura) break
  }

  return mejor
}

/*
 * EL GPS SE CALIENTA DESDE QUE SE ABRE LA CÁMARA.
 *
 * Mientras el técnico encuadra, el teléfono ya va afinando; al disparar, el
 * sello usa lo que haya conseguido y la foto no se queda esperando diez
 * segundos. Vale un minuto: después se vuelve a pedir.
 */
let calentada: { desde: number; promesa: Promise<LecturaUbicacion | null> } | null = null

export function calentarUbicacion(): void {
  if (calentada && Date.now() - calentada.desde < 60_000) return
  calentada = { desde: Date.now(), promesa: mejorPosicion() }
}

/**
 * La calentada si es de este último minuto —ya buscó la mejor durante diez
 * segundos: buscar otros diez solo haría esperar la foto—; si no, se busca
 * una ahora.
 */
export async function posicionPrecisa(): Promise<LecturaUbicacion | null> {
  if (!calentada || Date.now() - calentada.desde >= 60_000) {
    calentada = { desde: Date.now(), promesa: mejorPosicion() }
  }
  return calentada.promesa
}
